import {IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import type {FC, ReactNode} from 'react'
import {useState} from 'react'

export interface ActionMenuItem {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
}

interface Props {
  items: ActionMenuItem[]
  /** Tooltip + aria-label for the trigger. */
  tooltip: string
}

/**
 * Shared pop-out actions menu for the chat widgets (charts + tables). A kebab button
 * floated in the host's top-right (both hosts are `position: relative`; the resize grip
 * owns the bottom-right), opening a menu of actions -- so more export/share options can
 * be added per widget without changing this component. The host owns each action's
 * behaviour (the menu just closes after invoking it), so a clipboard write in an
 * `onClick` still runs inside the user gesture.
 */
const ActionsMenu: FC<Props> = ({items, tooltip}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const runAction = (item: ActionMenuItem) => (): void => {
    item.onClick()
    setAnchorEl(null)
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={tooltip}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 3,
            borderRadius: 1,
            boxShadow: 1,
            bgcolor: 'background.paper',
            '&:hover': {bgcolor: 'background.paper'},
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      >
        {items.map((item) => (
          <MenuItem key={item.key} onClick={runAction(item)} disabled={item.disabled}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default ActionsMenu
